/**
 * An approval card, and the brakes list beside it.
 *
 * The card is the console's: what was asked, which rule fired, what it touches,
 * and what happens if it is refused. The six-digit field is the second factor —
 * present because an operator *required* it, not because the page wanted
 * another input to draw. It is inert here; this is a picture of the console, not
 * a login for it.
 *
 * The brakes table underneath is ADR-0023, and it is the honest half of this
 * section. Unsupervised, the conductor may only stop work and summon a person.
 * Nothing that starts work or spends a subscription runs without a human, so the
 * table has a "can" column that is deliberately short and a "waits" column that
 * is deliberately longer.
 */
import { Panel, Badge } from '../../chrome'
import { Stamp, Stagger } from '../../kit'
import { useT } from '../../i18n/context'
import './ApprovalGate.css'

/** internal/domain — the conductor's unsupervised repertoire, and what is not in it. */
const BRAKES = {
  can: ['pause_mission', 'stop_session', 'raise_alert', 'request_human'],
  cannot: ['start_session', 'route_task', 'approve_task', 'switch_account', 'merge_branch'],
} as const

export function ApprovalGate() {
  const t = useT()

  return (
    <div className="kl-appr">
      <Panel
        framed
        className="kl-appr__card"
        title="mercury-api · migrations/0042_add_index.sql"
        actions={<Badge tone="warn">high risk</Badge>}
      >
        <dl className="kl-appr__facts">
          <dt>rule</dt>
          <dd className="k-mono">schema_change_requires_human</dd>
          <dt>touches</dt>
          <dd className="k-mono">mercury-api · 1 table · 0 rows destroyed</dd>
          <dt>on refusal</dt>
          <dd className="k-mono">task → BLOCKED · session parked at boundary</dd>
          <dt>requested by</dt>
          <dd className="k-mono">01JQ8F3K7P · codex-ops-1</dd>
        </dl>

        <div className="kl-appr__mfa">
          <label className="kl-appr__mfaLabel" htmlFor="kl-appr-otp">
            second factor
          </label>
          {/* Inert and marked so: a live-looking field on a marketing page
              invites somebody to type a real code into it. */}
          <input
            id="kl-appr-otp"
            className="kl-appr__otp k-mono"
            type="text"
            inputMode="numeric"
            value="4 8 1 · 9 0 6"
            readOnly
            tabIndex={-1}
            aria-hidden="true"
          />
        </div>

        <div className="kl-appr__actions">
          <Stamp>
            <span className="kl-appr__approve">{t('ui.approve')}</span>
          </Stamp>
          <span className="kl-appr__reject">{t('ui.reject')}</span>
        </div>
      </Panel>

      <Panel className="kl-appr__brakes" title={t('approval.brakeTitle')}>
        <p className="kl-appr__brakeSub">{t('approval.brakeSub')}</p>

        <p className="kl-appr__col k-label">{t('approval.brakeCan')}</p>
        <Stagger as="ul" childAs="li" className="kl-appr__list">
          {BRAKES.can.map((a) => (
            <span key={a} className="kl-appr__act kl-appr__act--can k-mono">
              {a}
            </span>
          ))}
        </Stagger>

        <p className="kl-appr__col kl-appr__col--wait k-label">{t('approval.brakeCannot')}</p>
        <Stagger as="ul" childAs="li" className="kl-appr__list">
          {BRAKES.cannot.map((a) => (
            <span key={a} className="kl-appr__act kl-appr__act--wait k-mono">
              {a}
            </span>
          ))}
        </Stagger>
      </Panel>
    </div>
  )
}
